import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Link2,
  Mail,
  MessageSquare,
  RefreshCw,
  Share2,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useDailyReportStore } from "../store/useDailyReportStore";
import DailyReportView from "../components/reports/DailyReportView";

const DailyReportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { current, fetchById, shareReport, regenerate, loading } =
    useDailyReportStore();

  const [shareOpen, setShareOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const [includeSlack, setIncludeSlack] = useState(false);
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (id) fetchById(id);
  }, [id, fetchById]);

  // Shared externally — always the neutral platform domain, never the CRE host.
  const publicUrl = current
    ? `https://omniworkspace.app/reports/public/${current.shareToken}`
    : "";

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied");
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  const handleShare = async () => {
    if (!id) return;
    const list = emails
      .split(/[,;\s\n]+/)
      .map((e) => e.trim())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (!list.length && !includeSlack) {
      toast.error("Add at least one email or enable Slack");
      return;
    }
    setSending(true);
    const ok = await shareReport(id, { emails: list, slack: includeSlack });
    setSending(false);
    if (ok) {
      setShareOpen(false);
      setEmails("");
      setIncludeSlack(false);
      fetchById(id);
    }
  };

  const handleRegen = async () => {
    if (!id) return;
    setRegenerating(true);
    await regenerate(id);
    await fetchById(id);
    setRegenerating(false);
  };

  if (loading && !current) {
    return (
      <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
        <div className="max-w-5xl mx-auto text-center text-slate-400 py-20">
          <Loader2 size={22} className="inline animate-spin mr-2" />
          Loading report...
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
        <div className="max-w-5xl mx-auto text-center text-slate-400 py-20">
          Report not found.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <button
            onClick={() => navigate("/reports/daily")}
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 hover:text-slate-900"
          >
            <ChevronLeft size={14} />
            All Reports
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Link2 size={14} />
              Copy Link
            </button>
            <button
              onClick={handleRegen}
              disabled={regenerating}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw
                size={14}
                className={regenerating ? "animate-spin" : ""}
              />
              Recompute
            </button>
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0058c3] hover:opacity-90 text-white rounded-lg text-[12px] font-semibold"
            >
              <Share2 size={14} />
              Share
            </button>
          </div>
        </div>

        <DailyReportView report={current} />

        <div className="mt-5 bg-white border border-slate-200 rounded-xl p-4 text-[12px] text-slate-500 flex items-center gap-2 break-all">
          <Link2 size={14} className="shrink-0" />
          <span className="font-semibold text-slate-700">Public link:</span>
          <span className="font-mono truncate">{publicUrl}</span>
        </div>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShareOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Share2 size={20} className="text-[#0058c3]" />
              <h2 className="text-[18px] font-extrabold text-slate-900">
                Share Report
              </h2>
            </div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              Email recipients
            </label>
            <textarea
              rows={3}
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="manager@example.com, lead@example.com"
              className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md resize-none mb-3"
            />
            <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={includeSlack}
                onChange={(e) => setIncludeSlack(e.target.checked)}
              />
              <MessageSquare size={14} className="text-violet-600" />
              Also post to Slack
            </label>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShareOpen(false)}
                disabled={sending}
                className="px-4 py-2 text-[12px] font-semibold border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleShare}
                disabled={sending}
                className="px-4 py-2 text-[12px] font-semibold bg-[#0058c3] hover:opacity-90 text-white rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail size={14} />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReportDetailPage;
